import PropTypes from 'prop-types';
import React from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import {
  OlsConfigActions,
  OlsConfigStore,
  RouteHandler,
  ShopViewConstants,
  ScrollWidgetActions
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import {
  CategoryListStore,
  ShopStore,
  ProductActions,
  ProductListStore,
  CategoryActions
} from '@wsb/guac-widget-shared/lib/common/ols-core/shop-bundle';

import CategoryList from '../../shop/components/categories/CategoryList';
import SortDropdown from '../../shop/components/products/SortDropdown';
import ProductListData from './ProductListData';
import ProductListWrapper from '../../shop/components/products/ProductListWrapper';

import dataAids from '../../constants/dataAids';
import { initWidgets } from '../../shop/utils/YotpoUtils';
import { Fetching, ErrorMessage } from '../../shop/components/lazy';
import * as Metrics from '../../shop/components/utils/trackMetric';
import * as MetricEvents from '../../constants/metrics';
import { mapContextToProps } from '../../shop/components/OlsShopContext';

import { DigitalWalletStore, PoyntPaymentRequestActions } from '../../shop/utils/ols-core';

function hideEmptyStarsContainer() {
  const selector = '.ratings-review-wrapper.product-list .write-review-btn-hidden';
  const noReviewElements = document.querySelectorAll(selector);
  for (const reviewElement of noReviewElements) {
    // NEMO-8659: hide empty stars on Product List page
    reviewElement.parentNode.style.display = 'none';
  }
}

const styles = {
  wrapper: {
    position: 'relative'
  },
  subHeaderWrapper: {
    'marginBottom': 'large',
    'textAlign': 'left',
    '@xs': {
      alignSelf: 'flex-start'
    }
  },
  titleResults: {
    marginLeft: 'small'
  },
  pagination: {
    'display': 'flex',
    'marginTop': 'small',
    'justifyContent': 'center',
    '@sm': {
      justifyContent: 'flex-end'
    }
  },
  showInDesktop: {
    'display': 'none',
    '@sm': {
      display: 'flex'
    }
  },
  showInMobile: {
    'display': 'flex',
    '@sm': {
      display: 'none'
    }
  },
  categoriesCell: {
    '@md': {
      marginTop: 'xxsmall',
      paddingBottom: 'xlarge'
    },
    'paddingBottom': '0 !important',
    'position': 'relative',
    'display': 'flex'
  },
  productNotFound: {
    backgroundColor: 'section',
    padding: 'small',
    marginTop: 'medium',
    marginBottom: 'medium'
  },
  productNotFoundText: {
    fontSize: 'medium',
    textAlign: 'center'
  },
  categoryName: {
    'display': 'flex',
    'alignItems': 'flex-start',
    'flexDirection': 'column',
    'marginBottom': 'medium',
    '@xs-only': {
      alignItems: 'center',
      textAlign: 'center'
    }
  }
};

export class ProductList extends React.Component {
  constructor() {
    super(...arguments);
    this.onStoreChange = this.onStoreChange.bind(this);
    this.updateStateFromStore = this.updateStateFromStore.bind(this);
    this.onCategoryStoreChange = this.onCategoryStoreChange.bind(this);
    this.onPaginationPageChange = this.onPaginationPageChange.bind(this);
    this.onDigitalWalletChange = this.onDigitalWalletChange.bind(this);
    this.state = this.getStoreState();
  }

  getStoreState() {
    const {
      error,
      errorRetryable,
      products,
      currentPage,
      pages,
      totalCount,
      fetching: fetchingProducts,
      fetched: fetchedProducts
    } = ProductListStore.getState();
    const { selectedTaxonPermalink, routePath, searchKeywords, sortOption } = ShopStore.getState();
    const {
      taxons,
      fetching: fetchingCategories,
      fetched: fetchedCategories
    } = CategoryListStore.getState();
    const { configWasLoaded } = OlsConfigStore.getState();

    return {
      configWasLoaded,
      fetchedProducts,
      fetchedCategories,
      fetchingProducts,
      fetchingCategories,
      error,
      configError: OlsConfigStore.getState().error,
      errorRetryable,
      products,
      currentPage,
      pages,
      routePath,
      searchKeywords,
      sortOption,
      selectedTaxonPermalink,
      taxons,
      totalCount,
      digitalWalletIsAvailable: this.checkDigitalWalletAvailability()
    };
  }

  UNSAFE_componentWillMount() {
    ProductListStore.addListener('change', this.onStoreChange);
    CategoryListStore.addListener('change', this.onCategoryStoreChange);
    OlsConfigStore.addListener('change', this.onStoreChange);
    DigitalWalletStore.addListener('change', this.onDigitalWalletChange);
    this.initDigitalWallet();
  }

  componentWillUnmount() {
    window.removeEventListener('olsProductsUpdated', this.handleProductListReload);
    window.removeEventListener('finishLoadingProducts', this.updateStateFromStore);
    ProductListStore.removeListener('change', this.onStoreChange);
    CategoryListStore.removeListener('change', this.onCategoryStoreChange);
    OlsConfigStore.removeListener('change', this.onStoreChange);
    DigitalWalletStore.removeListener('change', this.onDigitalWalletChange);
  }

  componentDidUpdate() {
    initWidgets(hideEmptyStarsContainer, 'hideEmptyStarsListing');
    this.verifyPermalinkParam();
  }

  verifyPermalinkParam = () => {
    const { routePath, selectedTaxonPermalink, fetchingProducts, configWasLoaded } = this.state;
    const defaultCategory = OlsConfigStore.getDefaultCategory();
    const validRoute = routePath === RouteHandler.PRODUCTS_ROUTE || routePath === null;
    const shouldRefetchProducts =
      validRoute &&
      configWasLoaded &&
      defaultCategory &&
      !selectedTaxonPermalink &&
      !fetchingProducts &&
      !this.props.useStaticData;

    if (shouldRefetchProducts) {
      setTimeout(() => {
        ProductActions.loadProducts({ taxonPermalink: defaultCategory });
      }, 0);
    }
  };

  onStoreChange() {
    this.setState(this.getStoreState());
    window.setTimeout(() => this.loadProducts(), 0);
  }

  onCategoryStoreChange() {
    this.setState(this.getStoreState());

    setTimeout(() => {
      this.loadCategories();
    }, 0);
  }

  checkDigitalWalletAvailability() {
    const { poynt_config } = OlsConfigStore.getState();
    const { applePayIsAvailable, googlePayIsAvailable, poyntWasLoaded } =
      DigitalWalletStore.getState();

    if (poyntWasLoaded) {
      return (applePayIsAvailable || googlePayIsAvailable) && poynt_config.with_apple_pay;
    }
  }

  onDigitalWalletChange() {
    const { poyntWasLoaded, availabilityWasChecked, inSession } = DigitalWalletStore.getState();
    const { poynt_config } = OlsConfigStore.getState();

    if (poyntWasLoaded && poynt_config.with_apple_pay) {
      if (availabilityWasChecked) {
        this.setState({
          inSession,
          digitalWalletIsAvailable: this.checkDigitalWalletAvailability()
        });
      } else {
        setTimeout(() => PoyntPaymentRequestActions.checkAvailability(), 0);
      }
    }
  }

  initDigitalWallet() {
    const { poyntWasLoaded } = DigitalWalletStore.getState();
    const { configWasLoaded, poynt_config } = OlsConfigStore.getState();

    if (poyntWasLoaded || !configWasLoaded) return;

    if (poynt_config.with_apple_pay) {
      PoyntPaymentRequestActions.init();
    }
  }

  updateStateFromStore() {
    this.setState(this.getStoreState());
  }

  componentDidMount() {
    window.addEventListener('olsProductsUpdated', this.handleProductListReload);
    window.addEventListener('finishLoadingProducts', this.updateStateFromStore);
    Metrics.track(MetricEvents.PRODUCT_LIST_IMPRESSION);
    setTimeout(() => {
      this.loadProducts();
      this.loadCategories();
    }, 0);
  }

  loadCategories = () => {
    if (this.props.useStaticData) return;
    CategoryActions.loadCategories();
  };

  loadProducts = () => {
    const { configWasLoaded } = OlsConfigStore.getState();
    const { fetched } = ProductListStore.getState();
    if (!configWasLoaded || fetched || this.props.useStaticData) return;
    ProductActions.loadProducts({ taxonPermalink: OlsConfigStore.getDefaultCategory() });
  };

  handleProductListReload = () => {
    // BEAR-2542: If going from free trial into OLS, we may need to attempt fetching config again
    const { configWasLoaded } = OlsConfigStore.getState();
    const queryParams = new URLSearchParams(window.location.search);
    const page = queryParams.get('page') || 1;

    if (!configWasLoaded) {
      window.setTimeout(() => {
        OlsConfigActions.clearError();
        OlsConfigActions.loadConfig(true);
      }, 0);
    }
    window.setTimeout(
      () =>
        ProductActions.loadProducts({ taxonPermalink: OlsConfigStore.getDefaultCategory(), page }),
      0
    );
    this.loadCategories();
  };

  onPaginationPageChange(page) {
    const { selectedTaxonPermalink, routePath, searchKeywords, sortOption } = this.state;
    const { renderMode, currentView } = this.props;
    const data = {
      page: page,
      keywords: searchKeywords,
      routePath,
      taxonPermalink: selectedTaxonPermalink
    };
    if (sortOption) {
      data.sortOption = sortOption;
    }
    // RouteHandler handles the navigation based on the view and
    // it loads different product pages for product listing view only
    const view = renderMode === 'EDIT' ? ShopViewConstants.PRODUCT_LISTING : currentView;
    RouteHandler.navigate(view, data);
    ScrollWidgetActions.scrollShopWidget();
  }

  render() {
    const {
      currentView,
      layoutDesktopColumns,
      layoutMobileColumns,
      defaultSortOption,
      renderMode,
      useStaticData,
      categoryDetails,
      translate,
      featureFlags: { enableOlsProductSorting = false, isOlsQuickViewOn },
      imageCropMethod,
      galleryShowBorder,
      galleryImageShape,
      smartRibbonsSale,
      smartRibbonsBestseller,
      smartRibbonsNewProduct,
      smartRibbonsSaleOption,
      smartRibbonsBestsellerOption,
      smartRibbonsNewProductOption,
      smartRibbonStyle,
      viewDevice,
      section,
      showQuickView,
      pdpImageCropMethod,
      pdpImageShape,
      useBruce,
      productsUrl
    } = this.props;
    const {
      products = [],
      error,
      configError,
      currentPage,
      pages,
      taxons,
      selectedTaxonPermalink,
      routePath,
      searchKeywords,
      sortOption,
      totalCount,
      fetchingProducts,
      fetchedProducts,
      fetchingCategories,
      fetchedCategories
    } = this.state;

    if (configError && configError.hasOwnProperty('status') && !useStaticData) {
      return <ErrorMessage message={ translate('products.error_fetching_product_data') } />;
    }
    if (!fetchedProducts || !fetchedCategories) {
      return <Fetching fetching={ true } pageTransition={ true } />;
    }

    // The most reliable way of determining whether a product was not found is to check the
    // current olsPage/queryparams. If we are on what looks like a PDP page but see this view, then
    // we can safely assume that the product was not found.
    const showProductNotFound =
      this.props.showProductNotFound ||
      /olsPage=products(\/|%2f)[\w]+/i.test(window.location.search) ||
      /\/ols\/products\/[\w]+/i.test(window.location.pathname);

    const { Block, Text } = UX2.Element;
    const { onPaginationPageChange } = this;
    const showCategories = taxons.length > 0;

    const isSearchView = currentView === ShopViewConstants.SEARCH_RESULTS;

    let resultsString;
    if (isSearchView) {
      if (totalCount === 0) {
        resultsString = 'no_search_results';
      } else if (totalCount === 1) {
        resultsString = 'search_results_page_singular';
      } else {
        resultsString = 'search_results_page_plural';
      }
    }

    let resultTitleString = 'no_search_results';
    if (isSearchView && totalCount > 0) {
      resultTitleString = 'search_results_page_title';
    }

    const resultsCountDetail = (renderClass = null) => {
      let resultStyle = {
        whiteSpace: 'nowrap'
      };

      if (!renderClass) {
        resultStyle = {
          ...resultStyle,
          marginLeft: 0
        };
      }

      return (
        totalCount > 0 && (
          <UX2.Element.Details
            tag='span'
            disabled={ true }
            children={ translate(`products.${resultsString}`, { count: totalCount }) }
            style={{ ...renderClass, ...resultStyle }}
          />
        )
      );
    };

    const searchResults = isSearchView && (
      <Block style={ styles.subHeaderWrapper }>
        <UX2.Group.Group style={ styles.showInDesktop }>
          <UX2.Element.Heading.Product
            data-aid={
              totalCount > 0
                ? dataAids.SHOP_TITLE_RENDERED
                : dataAids.SEARCH_NO_RESULTS_MESSAGE_RENDERED
            }
          >
            { translate(`products.${resultTitleString}`, { query: searchKeywords }) }
            { resultsCountDetail(styles.titleResults) }
          </UX2.Element.Heading.Product>
        </UX2.Group.Group>
        <Block style={ styles.showInMobile }>{ resultsCountDetail() } </Block>
      </Block>
    );

    const categories = showCategories && (
      <CategoryList
        renderMode={ renderMode }
        taxons={ taxons }
        selectedTaxonPermalink={ selectedTaxonPermalink }
        routePath={ routePath }
        isSearch={ isSearchView }
        currentSortOption={ sortOption }
      />
    );

    const categorySelected = !categoryDetails.image && categoryDetails.name && (
      <UX2.Group.Group>
        <UX2.Element.Heading.Product
          style={ styles.categoryName }
          data-aid={ dataAids.SHOP_CATEGORIES }
        >
          { categoryDetails.name }
          { categoryDetails.description && <Text children={ categoryDetails.description } /> }
        </UX2.Element.Heading.Product>
      </UX2.Group.Group>
    );

    const hasProducts = totalCount >= 2 && products.length > 0;
    const sortDropdown = ({ withBorder }) =>
      enableOlsProductSorting &&
      hasProducts && (
        <SortDropdown
          withBorder={ withBorder }
          renderMode={ renderMode }
          translate={ translate }
          currentView={ currentView }
          currentSortOption={ sortOption || defaultSortOption }
          currentPage={ currentPage }
          selectedTaxonPermalink={ selectedTaxonPermalink }
          routePath={ routePath }
          searchKeywords={ searchKeywords }
        />
      );

    const productListDataProps = {
      error,
      products,
      layoutDesktopColumns,
      layoutMobileColumns,
      showCategories,
      translate,
      imageCropMethod,
      galleryShowBorder,
      galleryImageShape,
      smartRibbonsSale,
      smartRibbonsBestseller,
      smartRibbonsNewProduct,
      smartRibbonsSaleOption,
      smartRibbonsBestsellerOption,
      smartRibbonsNewProductOption,
      smartRibbonStyle,
      isSearchView,
      renderMode,
      showProductNotFound,
      onPaginationPageChange,
      pages,
      currentPage,
      viewDevice,
      section,
      pdpImageCropMethod,
      pdpImageShape,
      isOlsQuickViewOn: isOlsQuickViewOn && showQuickView,
      useBruce,
      productsUrl
    };

    const productListWrapperProps = {
      searchResults,
      sortDropdown,
      categories,
      categorySelected,
      viewDevice,
      fetching: fetchingProducts || fetchingCategories
    };

    return (
      <ProductListWrapper { ...productListWrapperProps }>
        <ProductListData { ...productListDataProps } />
      </ProductListWrapper>
    );
  }
}

ProductList.propTypes = {
  imageCropMethod: PropTypes.string,
  currentView: PropTypes.any,
  layoutDesktopColumns: PropTypes.number,
  layoutMobileColumns: PropTypes.number,
  defaultSortOption: PropTypes.string,
  renderMode: PropTypes.string,
  products: PropTypes.object,
  useStaticData: PropTypes.bool,
  galleryShowBorder: PropTypes.bool,
  galleryImageShape: PropTypes.string,
  smartRibbonsSale: PropTypes.bool,
  smartRibbonsBestseller: PropTypes.bool,
  smartRibbonsNewProduct: PropTypes.bool,
  smartRibbonsSaleOption: PropTypes.string,
  smartRibbonsBestsellerOption: PropTypes.string,
  smartRibbonsNewProductOption: PropTypes.string,
  smartRibbonStyle: PropTypes.string,
  showSimilarProducts: PropTypes.bool,
  // Used primarily for testing:
  showProductNotFound: PropTypes.bool,
  categoryDetails: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string
  }).isRequired,
  translate: PropTypes.func,
  featureFlags: PropTypes.object,
  viewDevice: PropTypes.string,
  section: PropTypes.string,
  showQuickView: PropTypes.bool,
  pdpImageCropMethod: PropTypes.string,
  pdpImageShape: PropTypes.string,
  useBruce: PropTypes.bool,
  productsUrl: PropTypes.string
};

ProductList.defaultProps = {
  translate: () => {},
  featureFlags: {}
};

export default mapContextToProps(ProductList, context => ({
  featureFlags: context.featureFlags,
  translate: context.translate
}));
